import { isPlausibleEpochNs } from "./timestampMentionDetection";

/**
 * `ai-ts:<ns>` marks an inline, non-clickable timestamp produced by the
 * remarkTimestampMentions plugin from a bare `t=<n><unit>` prose mention.
 * `ai-ts-hl:<ns>` marks the same thing when the mention was originally
 * wrapped in an inline-code highlight (`` `t=...ns` `` or `t=\`...ns\``) —
 * the renderer preserves the code styling. Both are distinct from
 * `timestamp:<ns>` (an explicit clickable seek handle).
 */
export const isAIInlineTimestampLink = (url: string) => {
  return url.startsWith("ai-ts:") || url.startsWith("ai-ts-hl:");
};

export const isAIInlineTimestampHighlightedLink = (url: string) => {
  return url.startsWith("ai-ts-hl:");
};

/**
 * Extract the nanosecond payload from an `ai-ts:<ns>` or `ai-ts-hl:<ns>`
 * URL produced by {@link remarkTimestampMentions}.
 *
 * Returns `undefined` for two distinct failure modes (callers should treat
 * both as "render fallback"; the function does not distinguish):
 *
 *   - The URL is not an `ai-ts:` / `ai-ts-hl:` link at all. Use
 *     {@link isAIInlineTimestampLink} first if you need to disambiguate.
 *   - The URL has the right prefix but the payload is not a valid bigint
 *     (empty, non-numeric, etc.) — signals an upstream plugin defect.
 *   - The payload is a bigint but not a plausible epoch instant. The plugin
 *     never emits one (its detector rejects the value first), but the plugin
 *     is not the only source: a model can write the marker itself as literal
 *     markdown (`[10 s](ai-ts:10000000000)`), which reaches the renderer
 *     intact — `walk` skips existing link nodes and
 *     AgentThreadResponseMarkdown's `urlTransform` allowlists the scheme.
 *     Without this check that link renders ten billion nanoseconds as a
 *     wall-clock instant, the exact bug the detector's range closes.
 */
export const parseAIInlineTimestampNs = (url: string): bigint | undefined => {
  const prefix = ["ai-ts-hl:", "ai-ts:"].find((p) => url.startsWith(p));
  if (prefix === undefined) {
    return undefined;
  }
  const payload = url.slice(prefix.length);
  // Require a pure non-negative digit string: `remarkTimestampMentions`
  // emits exactly that (`m.ns.toString()`). Stricter than `BigInt(...)`,
  // which would coerce empty/whitespace to `0n` and accept leading
  // whitespace — both signal an upstream plugin defect.
  if (!/^\d+$/.test(payload)) {
    return undefined;
  }
  try {
    const ns = BigInt(payload);
    return isPlausibleEpochNs(ns) ? ns : undefined;
  } catch {
    return undefined;
  }
};
